"use client";

import { prepareForTranscription, SPEECH_SAMPLE_RATE } from "mediatools";
import { useTranslations } from "next-intl";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { baseName } from "../_components/format";
import { OptionRadioGroup } from "../_components/OptionRadioGroup";
import type { MediaOperation, OperationOptionsProps } from "./types";

interface OptimizeOptions {
  sampleRate: string;
  mono: boolean;
}

function OptimizeOptionsForm({
  options,
  setOptions,
  disabled,
}: OperationOptionsProps<OptimizeOptions>) {
  const t = useTranslations("mediaTools.options.optimizeAudio");
  const sampleRates = [
    {
      value: String(SPEECH_SAMPLE_RATE),
      label: t("sampleRates.16000.label"),
      hint: t("sampleRates.16000.hint"),
    },
    { value: "22050", label: t("sampleRates.22050.label") },
    { value: "44100", label: t("sampleRates.44100.label"), hint: t("sampleRates.44100.hint") },
  ];

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <OptionRadioGroup
        name="sample-rate"
        label={t("sampleRateLabel")}
        value={options.sampleRate}
        onChange={(sampleRate) => setOptions({ ...options, sampleRate })}
        options={sampleRates}
        disabled={disabled}
      />
      <div className="flex items-center gap-3">
        <Switch
          id="mono"
          checked={options.mono}
          onCheckedChange={(mono) => setOptions({ ...options, mono })}
          disabled={disabled}
        />
        <Label htmlFor="mono">{t("monoLabel")}</Label>
      </div>
    </div>
  );
}

export const optimizeAudioOperation: MediaOperation<OptimizeOptions> = {
  id: "optimize-audio",
  group: "audio",
  inputKinds: ["audio", "video"],
  outputKind: "audio",
  defaultOptions: { sampleRate: String(SPEECH_SAMPLE_RATE), mono: true },
  OptionsForm: OptimizeOptionsForm,
  run: async (ctx, options) => {
    if (!ctx.file) throw new Error("No file selected.");
    const pcm = await prepareForTranscription(ctx.file, {
      sampleRate: Number(options.sampleRate),
      mono: options.mono,
    });
    return { pcm, baseName: `${baseName(ctx.file.name)}-optimized`, inputBytes: ctx.file.size };
  },
};
